import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, Loader2, Lightbulb, Trash2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cmsCall } from "@/lib/cms";

const STATUS = {
  open: { label: "Open", klasse: "bg-amber-100 text-amber-900" },
  doorgevoerd: { label: "Doorgevoerd", klasse: "bg-emerald-100 text-emerald-800" },
  afgewezen: { label: "Afgewezen", klasse: "bg-secondary text-muted-foreground" },
};

const LAND = { nl: "Nederland", be: "België", de: "Duitsland", fr: "Frankrijk" };
const VELD = { verzendkosten: "Verzendkosten", minimum_bestelbedrag: "Minimum bestelbedrag", gratis_verzending_vanaf: "Gratis verzending vanaf", korting_percentage: "Korting bij direct betalen (%)" };

const Wijziging = ({ s }) => {
  const w = s.wijziging || {};
  if (s.soort === "pagina_tekst") {
    return (
      <div className="mt-3 space-y-2 text-sm" data-testid={`suggestie-wijziging-${s.id}`}>
        <p className="text-xs text-muted-foreground">
          Pagina <code className="font-mono">/{w.slug === "home" ? "" : w.slug}</code> · blok <code className="font-mono">{w.blok_id}</code> · veld{" "}
          <code className="font-mono">{w.veld}</code> · taal <code className="font-mono uppercase">{w.taal}</code>
        </p>
        <div className="grid gap-2 sm:grid-cols-[1fr_auto_1fr] sm:items-center">
          <div className="rounded-md border bg-secondary/60 p-3 text-muted-foreground line-through decoration-muted-foreground/50">{s.huidige_waarde || <em>leeg</em>}</div>
          <ArrowRight className="hidden h-4 w-4 text-muted-foreground sm:block" />
          <div className="rounded-md border border-primary/30 bg-primary/5 p-3 font-medium">{w.nieuwe_tekst}</div>
        </div>
      </div>
    );
  }
  if (s.soort === "site_instelling") {
    const eenheid = w.veld === "korting_percentage" ? "%" : "€";
    return (
      <p className="mt-3 text-sm" data-testid={`suggestie-wijziging-${s.id}`}>
        {VELD[w.veld] || w.veld}{w.land ? ` (${LAND[w.land] || w.land})` : ""}:{" "}
        <span className="text-muted-foreground line-through">{eenheid} {s.huidige_waarde ?? 0}</span>
        <ArrowRight className="mx-2 inline h-3.5 w-3.5 text-muted-foreground" />
        <strong>{eenheid} {w.waarde}</strong>
      </p>
    );
  }
  return null;
};

export const Suggesties = () => {
  const [lijst, setLijst] = useState(null);
  const [tab, setTab] = useState("open");
  const [busy, setBusy] = useState("");

  const laad = useCallback(() => {
    cmsCall("GET", "/beheer/claude/suggesties", null, { admin: true })
      .then((d) => setLijst(d.suggesties || []))
      .catch((e) => toast.error(e.message));
  }, []);
  useEffect(laad, [laad]);

  const beoordeel = async (s, actie) => {
    const opmerking = actie === "afwijzen" ? window.prompt("Reden voor Claude (optioneel):", "") : "";
    if (opmerking === null) return;
    setBusy(s.id);
    try {
      const res = await cmsCall("POST", `/beheer/claude/suggesties/${s.id}/${actie}`, { opmerking: opmerking || "" }, { admin: true });
      setLijst((l) => l.map((x) => (x.id === s.id ? res.suggestie : x)));
      toast.success(actie === "doorvoeren" ? (s.automatisch_toepasbaar ? "Suggestie doorgevoerd en toegepast" : "Suggestie gemarkeerd als doorgevoerd") : "Suggestie afgewezen");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy("");
    }
  };
  const verwijder = async (s) => {
    setBusy(s.id);
    try {
      await cmsCall("DELETE", `/beheer/claude/suggesties/${s.id}`, null, { admin: true });
      setLijst((l) => l.filter((x) => x.id !== s.id));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy("");
    }
  };

  if (!lijst) return null;
  const open = lijst.filter((s) => s.status === "open");
  const getoond = tab === "open" ? open : lijst.filter((s) => s.status !== "open");

  return (
    <Card className="p-5" data-testid="claude-suggesties">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 font-display text-base font-semibold">
            <Lightbulb className="h-4 w-4 text-primary" />Suggesties van Claude
            {open.length > 0 && <Badge className="rounded-full" data-testid="claude-suggesties-open-count">{open.length}</Badge>}
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Claude kan verbeteringen voorstellen maar niets zelf wijzigen. Tekst- en instellingsvoorstellen worden pas toegepast
            als jij ze hier doorvoert; adviezen pak je zelf op.
          </p>
        </div>
        <div className="inline-flex rounded-full border bg-background p-1">
          {[["open", `Open (${open.length})`], ["afgehandeld", `Afgehandeld (${lijst.length - open.length})`]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setTab(k)} className={`rounded-full px-3 py-1.5 text-sm font-medium ${tab === k ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`} data-testid={`claude-suggesties-tab-${k}`}>{l}</button>
          ))}
        </div>
      </div>

      {getoond.length === 0 ? (
        <p className="mt-4 rounded-md bg-secondary px-3 py-3 text-sm text-muted-foreground" data-testid="claude-suggesties-leeg">
          {tab === "open" ? "Geen open suggesties. Vraag Claude in een gesprek om verbeteringen voor te stellen." : "Nog geen afgehandelde suggesties."}
        </p>
      ) : (
        <ul className="mt-4 space-y-3">
          {getoond.map((s) => {
            const st = STATUS[s.status] || STATUS.open;
            return (
              <li key={s.id} className="rounded-lg border p-4" data-testid={`suggestie-${s.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${st.klasse}`}>{st.label}</span>
                      <span className="text-xs text-muted-foreground">{s.soort_label}</span>
                    </div>
                    <p className="mt-1.5 font-medium">{s.titel}</p>
                  </div>
                  <span className="text-xs text-muted-foreground">{new Date(s.ingediend_op).toLocaleString("nl-NL")}</span>
                </div>
                {s.toelichting && <p className="mt-2 whitespace-pre-line text-sm text-muted-foreground">{s.toelichting}</p>}
                <Wijziging s={s} />
                {s.status !== "open" && (
                  <p className="mt-3 text-xs text-muted-foreground">
                    {st.label} door {s.beoordeeld_door} op {new Date(s.beoordeeld_op).toLocaleString("nl-NL")}{s.opmerking ? ` · "${s.opmerking}"` : ""}
                  </p>
                )}
                <div className="mt-3 flex flex-wrap gap-2">
                  {s.status === "open" ? (
                    <>
                      <Button size="sm" onClick={() => beoordeel(s, "doorvoeren")} disabled={busy === s.id} data-testid={`suggestie-doorvoeren-${s.id}`}>
                        {busy === s.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}
                        {s.automatisch_toepasbaar ? "Doorvoeren" : "Opgepakt"}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => beoordeel(s, "afwijzen")} disabled={busy === s.id} data-testid={`suggestie-afwijzen-${s.id}`}>
                        <X className="mr-2 h-4 w-4" />Afwijzen
                      </Button>
                    </>
                  ) : (
                    <Button size="sm" variant="ghost" onClick={() => verwijder(s)} disabled={busy === s.id} data-testid={`suggestie-verwijderen-${s.id}`}>
                      <Trash2 className="mr-2 h-4 w-4" />Verwijderen
                    </Button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
};
